
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase, Article } from '../../../lib/supabase';

const NewsDetail: React.FC = () => {
  const [selectedLanguage, setSelectedLanguage] = useState('简体中文');
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    loadArticle();
  }, [id]);

  const loadArticle = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('id', id)
        .eq('status', 'published')
        .single();

      if (error) {
        // 如果数据库中没有找到，使用默认示例数据
        if (id === '1') {
          setArticle({
            id: '1',
            title: '印尼数字经济发展现状与前景分析',
            summary: '深入分析印度尼西亚数字经济的发展现状，探讨其在东南亚地区的重要地位以及未来发展机遇与挑战。',
            date: '2024-01-15',
            columnType: 'country',
            category: '印度尼西亚',
            link: 'https://mp.weixin.qq.com/s/OZ3EGdOmXg_UscQGwU5YIg',
            image: 'https://public.readdy.ai/ai/img_res/24c29fd39e5074ecfe3c4388da0a77c9.jpg',
            status: 'published',
            content: `## 核心数据

根据印尼统计局(BPS)最新发布的官方数据，印尼2023年国内生产总值(GDP)达到1.42万亿美元,同比增长5.05%。数字经济贡献率达到8.2%，年增长率22%，成为推动经济增长的重要引擎。

## 经济增长概况

### GDP增长表现

印尼统计局数据显示，2023年印尼GDP达到20,892万亿印尼盾（约1.42万亿美元），实际增长率为5.05%，超过政府设定的4.5-5.3%增长目标。这一增长主要得益于国内消费、投资和出口的协调发展。

**关键指标：**
- **GDP总量：** 1.42万亿美元（全球第16位）
- **增长率：** 5.05%（超出预期目标）
- **人均GDP：** 5,109美元（同比增长4.2%）

### 数字经济发展

Google-Temasek-Bain《东南亚数字经济报告2023》显示，印尼数字经济规模达到770亿美元，占GDP比重8.2%，年增长率22%。电商、数字支付、在线媒体和交通出行是主要增长领域。

**数字经济细分领域：**
- 电子商务：520亿美元（67%市场份额）
- 数字支付：180亿美元（23%市场份额）
- 在线媒体：45亿美元（6%市场份额）
- 交通出行：25亿美元（4%市场份额）

## 未来增长机遇

### 人口红利优势

印尼拥有2.77亿人口，其中65%为35岁以下年轻人群，为数字经济发展提供巨大市场潜力。联合国预测，到2030年印尼中产阶级人口将达到1.4亿。

### 基础设施建设

印尼政府推出"Making Indonesia 4.0"战略，计划投资4000亿美元用于数字基础设施建设。5G网络覆盖率预计2025年达到80%，为数字经济发展奠定基础。

### 政策支持

印尼政府出台《数字经济路线图2021-2024》，目标是到2024年数字经济对GDP贡献率达到12%。同时推出税收优惠、简化审批等政策支持数字企业发展。

## 专家观点

> "印尼数字经济的快速发展得益于庞大的人口基数、年轻的人口结构以及政府的政策支持。未来5年，印尼有望成为东南亚最大的数字经济体。"
> 
> *— 印尼经济研究院高级研究员*

---

**数据来源：** 印尼统计局(BPS)、印尼银行(BI)、印尼投资协调委员会(BKPM)、世界银行、Google-Temasek-Bain东南亚数字经济报告2023、国际货币基金组织(IMF)`
          });
        } else {
          setArticle(null);
        }
      } else {
        setArticle(data as Article);
      }
    } catch (error) {
      console.error('加载文章失败:', error);
      setArticle(null);
    } finally {
      setLoading(false);
    }
  };

  // 格式化Markdown内容为HTML
  const formatContent = (content: string) => {
    return content
      // 处理标题
      .replace(/^### (.*?)$/gm, '<h3 class="text-xl font-semibold mb-3 text-gray-800 mt-6">$1</h3>')
      .replace(/^## (.*?)$/gm, '<h2 class="text-2xl font-bold mb-4 mt-8">$1</h2>')
      .replace(/^# (.*?)$/gm, '<h1 class="text-3xl font-bold mb-6 mt-8">$1</h1>')

      // 处理粗体和斜体
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')

      // 处理图片
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="max-w-full h-auto rounded-lg my-6 shadow-sm" />')

      // 处理链接
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:text-blue-700 underline" target="_blank" rel="noopener noreferrer">$1</a>')

      // 处理列表
      .replace(/^- (.*?)$/gm, '<li class="mb-2">$1</li>')
      .replace(/(<li.*<\/li>)/s, '<ul class="list-disc pl-6 mb-4 space-y-2">$1</ul>')

      // 处理引用
      .replace(/^> (.*?)$/gm, '<blockquote class="border-l-4 border-blue-500 pl-4 italic text-gray-700 my-4">$1</blockquote>')

      // 处理段落
      .replace(/\n\n/g, '</p><p class="mb-4 leading-relaxed">')
      .replace(/^(.+)$/gm, '<p class="mb-4 leading-relaxed">$1</p>')

      // 清理多余的段落标签
      .replace(/<p class="mb-4 leading-relaxed"><\/p>/g, '')
      .replace(/<p class="mb-4 leading-relaxed">(<h[1-6])/g, '$1')
      .replace(/(<\/h[1-6]>)<\/p>/g, '$1')
      .replace(/<p class="mb-4 leading-relaxed">(<ul)/g, '$1')
      .replace(/(<\/ul>)<\/p>/g, '$1')
      .replace(/<p class="mb-4 leading-relaxed">(<blockquote)/g, '$1')
      .replace(/(<\/blockquote>)<\/p>/g, '$1');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <i className="ri-file-text-line text-6xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">文章未找到</h2>
          <p className="text-gray-600 mb-6">抱歉，您访问的文章不存在或已被删除。</p>
          <a 
            href="/zh/news" 
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            返回资讯列表
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 bg-[#071C74] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="adi-header-left">
            <a href="/">
              <img
                alt="ADI Logo"
                width={125}
                height={56}
                src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/3900b388860a5efa532a02080a385ec7.png"
                className="h-14 w-auto"
              />
            </a>
          </div>
          <div className="flex items-center space-x-4 text-white">
            <div className="relative">
              <div className="flex bg-white/10 backdrop-blur-sm rounded-full p-1">
                <button
                  className="px-4 py-2 text-white bg-white/20 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
                  onClick={() => {
                    setSelectedLanguage('简体中文');
                    setIsLanguageDropdownOpen(false);
                  }}
                >
                  简体中文
                </button>
                <a
                  href="/en/news"
                  className="px-4 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
                >
                  English
                </a>
              </div>
            </div>

            <a href="/flanexus" className="px-4 py-2 text-white hover:text-blue-200 transition-colors whitespace-nowrap">
              炎禾智联
            </a>

            <a href="/zh/news" className="px-4 py-2 text-white hover:text-blue-200 transition-colors">
              资讯
            </a>

            <a
              href="/zh/contact-us"
              className="px-4 py-2 border border-white rounded-full hover:bg-white hover:text-[#071C74] transition-colors text-white"
            >
              联系我们
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <nav className="text-sm text-gray-500 mb-4">
            <a href="/zh/news" className="hover:text-blue-600">资讯</a>
            <span className="mx-2">/</span>
            <span className="text-gray-900">{article.category}</span>
          </nav>
          
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
            {article.title}
          </h1>
          
          <div className="flex items-center space-x-4 text-gray-600 mb-8">
            <span>{article.date}</span>
            <span className="px-3 py-1 bg-blue-100 text-blue-600 text-sm rounded-full">
              {article.columnType === 'industry' ? '行业栏目' : '国家栏目'}
            </span>
            <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
              {article.category}
            </span>
          </div>

          {/* 文章摘要 */}
          {article.summary && (
            <div className="bg-blue-50 p-6 rounded-lg mb-8">
              <h2 className="text-lg font-semibold text-blue-900 mb-3">文章摘要</h2>
              <p className="text-blue-800 leading-relaxed">{article.summary}</p>
            </div>
          )}

          {/* 封面图片 */}
          {article.image && (
            <div className="mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-64 md:h-80 object-cover object-top rounded-lg shadow-sm"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* 原文链接 */}
          {article.link && (
            <div className="bg-gray-50 p-4 rounded-lg mb-8">
              <p className="text-sm text-gray-600 mb-2">原文链接：</p>
              <a
                href={article.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline break-all text-sm"
              >
                {article.link}
              </a>
            </div>
          )}
        </div>

        <article 
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: formatContent(article.content) }}
        />

        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <a 
              href="/zh/news"
              className="flex items-center text-blue-600 hover:text-blue-700 transition-colors"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              返回资讯列表
            </a>
            
            <div className="flex items-center space-x-4">
              <span className="text-gray-500">分享到：</span>
              <button className="text-gray-400 hover:text-blue-600 transition-colors">
                <i className="ri-wechat-line text-xl"></i>
              </button>
              <button className="text-gray-400 hover:text-blue-600 transition-colors">
                <i className="ri-weibo-line text-xl"></i>
              </button>
              <button className="text-gray-400 hover:text-blue-600 transition-colors">
                <i className="ri-linkedin-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <footer className="flex h-28 items-center justify-center bg-[#F7F7F7]">
        <span className="text-lg font-semibold text-black/85">战略合作伙伴: </span>
        <a
          href="https://www.flanexus.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-1 text-lg font-semibold underline decoration-[#2D66F4] text-[#2D66F4] hover:text-blue-700"
        >
          炎禾智联
        </a>
        <div className="ml-8">
          <a
            href="https://readdy.ai/?origin=logo"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
};

export default NewsDetail;
